import React, { useEffect, useRef, useState, useMemo } from "react";
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Container,
    Grid,
    Button,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Box,
    Modal,
    Backdrop,
    Fade,
    useMediaQuery,
    Paper,
    Link,
    Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import EmailIcon from "@mui/icons-material/Email";
import RoomIcon from "@mui/icons-material/Room";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import SportsHandballIcon from "@mui/icons-material/SportsHandball";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SportsMmaIcon from "@mui/icons-material/SportsMma";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";

import logo from "../logo.png";
import restaurant1 from "../res/restaurant1.jpg";
import restaurant2 from "../res/restaurant2.jpg";
import speisekarte from "../res/speisekarte.jpg";
import service1 from "../res/service1.jpg";
import service2 from "../res/service2.jpg";
import service3 from "../res/service3.jpg";
import service4 from "../res/service4.jpg";
import service5 from "../res/service5.jpg";

/* =========================
   CRA / Webpack: alle Bilder aus ../res/actual laden + numerisch sortieren
   Erwarteter Ordner: src/res/actual/
   ========================= */
const importAll = (r) => r.keys().map((k) => ({ key: k, src: r(k) }));

const getNumberFromKey = (k) => {
    // k ist sowas wie "./actual12.jpg"
    const name = String(k).toLowerCase();
    const m = name.match(/\d+/g);
    return m ? parseInt(m.join(""), 10) : Number.POSITIVE_INFINITY;
};

const loadActualImages = () => {
    try {
        const ctx = require.context("../res/actual", false, /\.(png|jpe?g|webp)$/i);
        const items = importAll(ctx);

        items.sort((a, b) => {
            const na = getNumberFromKey(a.key);
            const nb = getNumberFromKey(b.key);
            if (na !== nb) return na - nb;
            return a.key.localeCompare(b.key, "de");
        });

        return items.map((x) => x.src);
    } catch (e) {
        // Ordner fehlt oder leer -> kein Crash
        return [];
    }
};

/** Helfer: Telefonnummer für tel:-Link säubern */
const sanitizePhone = (s) => s.replace(/[^\d+]/g, "");

/** Helfer: Öffnungsinfo für heute */
const getTodayOpening = () => {
    const days = [
        "Sonntag",
        "Montag",
        "Dienstag",
        "Mittwoch",
        "Donnerstag",
        "Freitag",
        "Samstag",
    ];
    const todayName = days[new Date().getDay()];

    const hoursMap = {
        Montag: "Ruhetag",
        Dienstag: "Ruhetag",
        Mittwoch: "ab 17:00",
        Donnerstag: "ab 17:00",
        Freitag: "ab 17:00",
        Samstag: "ab 17:00",
        Sonntag: "ab 17:00",
    };

    const hours = hoursMap[todayName] || "";
    const isClosed =
        !hours ||
        hours.toLowerCase().includes("ruhe") ||
        hours.toLowerCase().includes("geschlossen");

    return { todayName, hours, isClosed };
};

/** Section mit seitlichen Streifen + Fade-Animation */
function SectionCard({
                         id,
                         title,
                         children,
                         stripeColor = "#eeeeee",
                         mb = { xs: 4, md: 6 },
                     }) {
    return (
        <Fade in timeout={700}>
            <Paper
                id={id}
                elevation={3}
                sx={{
                    position: "relative",
                    p: { xs: 3, md: 4 },
                    mb,
                    borderRadius: 2,
                    overflow: "hidden",
                    scrollMarginTop: { xs: 72, sm: 80, md: 96 },
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: { xs: 8, md: 12 },
                        backgroundColor: stripeColor,
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: { xs: 8, md: 12 },
                        backgroundColor: stripeColor,
                    },
                }}
            >
                {title && (
                    <Typography variant="h4" sx={{ color: "primary.main", mb: 2.5 }}>
                        {title}
                    </Typography>
                )}
                {children}
            </Paper>
        </Fade>
    );
}

export default function ZumAltenRathausApp() {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [reserveOpen, setReserveOpen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState(null);

    // Mobile Action-Bar: ausblenden, sobald Footer sichtbar ist
    const footerRef = useRef(null);
    const [hideMobileBar, setHideMobileBar] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const sections = [
        { id: "restaurant", label: "Restaurant" },
        { id: "aktuelles", label: "Aktuelles" },
        { id: "service", label: "Service" },
        { id: "speisekarte", label: "Speisekarte" },
        { id: "vereinslokal", label: "Vereinslokal" },
        { id: "anfahrt", label: "Anfahrt" },
        { id: "kontakt", label: "Kontakt" },
    ];

    const contact = {
        address: "Wilhelmstr. 161, 42489 Wülfrath",
        phone: "02058 - 17 98 687",
        email: "ZumAltenRathaus@gmx.de",
    };

    const socialLinks = {
        instagram: "https://www.instagram.com/zum_alten_rathaus_wuelfrath/",
        facebook: "https://www.facebook.com/mona.hartmann.754/",
    };

    const openingHours = [
        { day: "Montag", hours: "Ruhetag" },
        { day: "Dienstag", hours: "Ruhetag" },
        { day: "Mittwoch", hours: "ab 17:00" },
        { day: "Donnerstag", hours: "ab 17:00" },
        { day: "Freitag", hours: "ab 17:00" },
        { day: "Samstag", hours: "ab 17:00" },
        { day: "Sonntag", hours: "ab 17:00" },
        { day: "(Veranstaltungen auch an Ruhetagen möglich)", hours: "" },
    ];

    const restaurantImages = [restaurant1, restaurant2];
    const serviceImages = [service1, service2, service3, service4, service5];

    const todayOpening = getTodayOpening();

    // Actual Images nur einmal zur Laufzeit aufbauen
    const actualImages = useMemo(() => loadActualImages(), []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % restaurantImages.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [restaurantImages.length]);

    // Beobachtet den Footer: sobald sichtbar -> Action-Bar ausblenden
    useEffect(() => {
        if (!footerRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setHideMobileBar(entry.isIntersecting);
            },
            { root: null, threshold: 0.12 }
        );

        observer.observe(footerRef.current);
        return () => observer.disconnect();
    }, []);

    const scrollTo = (id) =>
        document
            .getElementById(id)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });

    const openLightbox = (src) => {
        setLightboxSrc(src);
        setLightboxOpen(true);
    };

    return (
        <Box sx={{ position: "relative", minHeight: "100vh" }}>
            {/* Durchgehender Seitenhintergrund */}
            <Box
                aria-hidden
                sx={{
                    position: "fixed",
                    inset: 0,
                    bgcolor: theme.palette.grey[100],
                    zIndex: -1,
                }}
            />

            {/* HEADER */}
            <AppBar
                position="sticky"
                sx={{
                    backgroundColor: "primary.main",
                    backdropFilter: "blur(8px)",
                }}
            >
                <Toolbar sx={{ px: { xs: 2, md: 4 }, minHeight: 70 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                            component="img"
                            src={logo}
                            alt="Logo"
                            loading="lazy"
                            decoding="async"
                            sx={{
                                padding: "5px",
                                width: 60,
                                height: 40,
                                borderRadius: "50%",
                                bgcolor: "#F5F5DC",
                            }}
                        />
                        <Box>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, letterSpacing: 0.3 }}
                            >
                                Zum Alten Rathaus
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ opacity: 0.9, display: { xs: "none", sm: "block" } }}
                            >
                                Wülfrath · Restaurant & Vereinslokal
                            </Typography>
                        </Box>
                    </Box>

                    {!isMobile && (
                        <Box
                            sx={{
                                ml: "auto",
                                pr: { xs: 2, md: 5 },
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            {sections.map((s) => (
                                <Button
                                    key={s.id}
                                    color="inherit"
                                    onClick={() => scrollTo(s.id)}
                                    sx={{
                                        fontWeight: 400,
                                        opacity: 0.9,
                                        "&:hover": { opacity: 1 },
                                    }}
                                >
                                    {s.label}
                                </Button>
                            ))}
                            <Button
                                variant="contained"
                                startIcon={<RestaurantIcon />}
                                onClick={() => setReserveOpen(true)}
                                sx={{
                                    bgcolor: "secondary.main",
                                    "&:hover": { bgcolor: "primary.main" },
                                }}
                            >
                                Reservieren
                            </Button>
                        </Box>
                    )}

                    {isMobile && (
                        <IconButton
                            onClick={() => setDrawerOpen(true)}
                            sx={{ ml: "auto", color: "common.white" }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                </Toolbar>
            </AppBar>

            {/* Drawer mobil */}
            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 260, bgcolor: "#f4f4f4", height: "100%" }}>
                    <List>
                        {sections.map((s) => (
                            <ListItem
                                key={s.id}
                                disablePadding
                                onClick={() => {
                                    scrollTo(s.id);
                                    setDrawerOpen(false);
                                }}
                            >
                                <ListItemText primary={s.label} sx={{ px: 2, py: 1.5 }} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Drawer>

            {/* Inhalt */}
            <Box component="main" sx={{ py: { xs: 6, md: 8 } }}>
                <Container maxWidth="lg">
                    {/* 1) RESTAURANT */}
                    <SectionCard id="restaurant" stripeColor="#eaeaea">
                        <Typography
                            sx={{ color: "primary.main", fontSize: "27px", textAlign: "center" }}
                        >
                            <b>
                                Restaurant - Herzlich willkommen auf meiner Seite 'Zum Alten Rathaus' - Eure Mona
                            </b>
                        </Typography>
                        <br />
                        <Box
                            sx={{
                                position: "relative",
                                height: { xs: 300, md: 420 },
                                borderRadius: 2,
                                overflow: "hidden",
                                boxShadow: 6,
                            }}
                        >
                            <Box
                                component="img"
                                src={restaurantImages[currentSlide]}
                                alt="Restaurant"
                                loading="lazy"
                                decoding="async"
                                onClick={() => openLightbox(restaurantImages[currentSlide])}
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    transition: "opacity 0.8s ease-in-out",
                                    cursor: "zoom-in",
                                }}
                            />
                            <IconButton
                                onClick={() =>
                                    setCurrentSlide(
                                        (currentSlide - 1 + restaurantImages.length) %
                                        restaurantImages.length
                                    )
                                }
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: 20,
                                    transform: "translateY(-50%)",
                                    bgcolor: "rgba(255,255,255,0.85)",
                                    "&:hover": { bgcolor: "#fff" },
                                }}
                            >
                                <ArrowBackIosNewIcon />
                            </IconButton>

                            <IconButton
                                onClick={() =>
                                    setCurrentSlide((currentSlide + 1) % restaurantImages.length)
                                }
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    right: 20,
                                    transform: "translateY(-50%)",
                                    bgcolor: "rgba(255,255,255,0.85)",
                                    "&:hover": { bgcolor: "#fff" },
                                }}
                            >
                                <ArrowForwardIosIcon />
                            </IconButton>

                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 16,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    display: "flex",
                                    gap: 1,
                                }}
                            >
                                {restaurantImages.map((_, i) => (
                                    <Box
                                        key={i}
                                        onClick={() => setCurrentSlide(i)}
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: "50%",
                                            cursor: "pointer",
                                            bgcolor: i === currentSlide ? "secondary.main" : "#e0e0e0",
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </SectionCard>

                    {/* 2) AKTUELLES */}
                    <SectionCard
                        id="aktuelles"
                        title="Aktuelles"
                        stripeColor="rgba(156,67,67,0.18)"
                    >
                        <Box
                            sx={{
                                width: 100,
                                height: 6,
                                borderRadius: 999,
                                background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                                mb: 4,
                                boxShadow: "0 2px 10px rgba(106,26,26,0.18)",
                            }}
                        />

                        <Grid container spacing={4} alignItems="center" justifyContent="center">
                            {actualImages.length === 0 ? (
                                <Grid item xs={12}>
                                    <Typography sx={{ color: "text.secondary", textAlign: "center" }}>
                                        Schauen Sie gerne bald wieder vorbei – Derzeit stehen keine Flyer zur Verfügung.
                                    </Typography>
                                </Grid>
                            ) : (
                                actualImages.map((img, idx) => (
                                    <Grid item xs={12} sm={6} md={4} key={idx}>
                                        <Box
                                            component="img"
                                            src={img}
                                            alt={`Aktuelles ${idx + 1}`}
                                            loading="lazy"
                                            decoding="async"
                                            onClick={() => openLightbox(img)}
                                            sx={{
                                                display: "block",
                                                width: "100%",
                                                maxWidth: 320,
                                                height: 350,
                                                objectFit: "cover",
                                                mx: "auto",
                                                borderRadius: 2,
                                                boxShadow: 6,
                                                cursor: "zoom-in",
                                            }}
                                        />
                                    </Grid>
                                ))
                            )}
                        </Grid>
                    </SectionCard>

                    {/* 3) SERVICE */}
                    <SectionCard id="service" title="Service" stripeColor="#eaeaea">
                        <Typography sx={{ color: "text.secondary", fontWeight: "bold" }}>
                            Ihr Anlass – unser Raum: Veranstaltungen jeder Art
                        </Typography>
                        <Typography sx={{ color: "text.secondary" }}>
                            Ob intime Feier im kleinen Kreis oder großes Fest mit vielen Gästen – unser Restaurant bietet den idealen
                            Rahmen für jede Art von Veranstaltung. Mit flexiblem Raumkonzept, liebevoller Ausstattung und individuell
                            abgestimmten Speisen sorgen wir dafür, dass Ihr Event unvergesslich wird.
                            <br />
                            <br />
                            Beispiele für Veranstaltungen, die wir gerne für Sie ausrichten:
                            <ul>
                                <li>
                                    <b>Private Anlässe:</b> Geburtstage, Jubiläen, Taufen, Kommunionen, Konfirmationen, Hochzeiten,
                                    Verlobungsfeiern, Familienfeiern
                                </li>
                                <li>
                                    <b>Business-Events:</b> Firmenfeiern, Tagungen, Seminare, Workshops, Produktpräsentationen, Team-Events,
                                    Geschäftsessen
                                </li>
                                <li>
                                    <b>Saisonale &amp; thematische Events:</b> Weihnachtsfeiern, Silvesterveranstaltungen, Oster- und
                                    Pfingstbrunch, Themenabende, After-Work-Treffen
                                </li>
                                <li>
                                    <b>Besondere Momente:</b> Abschiedsfeiern, Willkommensfeiern, Examensfeiern, Preisehrungen, Babypartys
                                </li>
                            </ul>
                            Wir unterstützen Sie von der ersten Idee bis zum letzten Gast – individuell, zuverlässig und mit
                            Leidenschaft für Gastfreundschaft. Nach Absprache können vorher organisierte Veranstaltungen auch an
                            Ruhetagen stattfinden.
                        </Typography>
                        <br />
                        <Grid container spacing={3} alignItems="center" justifyContent="center">
                            {serviceImages.map((image, i) => (
                                <Grid item key={image ?? i}>
                                    <Box
                                        component="img"
                                        src={image}
                                        alt={`Service ${i + 1}`}
                                        loading="lazy"
                                        decoding="async"
                                        onClick={() => openLightbox(image)}
                                        sx={{
                                            display: "block",
                                            width: 170,
                                            height: 220,
                                            objectFit: "cover",
                                            borderRadius: 0.8,
                                            boxShadow: 6,
                                            cursor: "zoom-in",
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>

                    {/* 4) SPEISEKARTE */}
                    <SectionCard id="speisekarte" title="Speisekarte" stripeColor="#eaeaea">
                        <Box
                            component="img"
                            src={speisekarte}
                            alt="Speisekarte"
                            loading="lazy"
                            decoding="async"
                            onClick={() => openLightbox(speisekarte)}
                            sx={{
                                display: "block",
                                width: "100%",
                                maxWidth: 500,
                                mx: "auto",
                                borderRadius: 2,
                                boxShadow: 5,
                                cursor: "zoom-in",
                            }}
                        />
                        <br />
                        <Typography sx={{ textAlign: "center" }}>
                            Zusätzlich bieten wir wechselnde Tagesgerichte an.
                        </Typography>
                    </SectionCard>

                    {/* 4.5) VEREINSLOKAL */}
                    <SectionCard id="vereinslokal" title="Vereinslokal" stripeColor="rgba(156,67,67,0.12)">
                        <Typography sx={{ color: "text.secondary", mb: 3 }}>
                            Wir sind stolzes Vereinslokal – hier treffen sich Fans, Spielerinnen &amp; Spieler nach Heim- und
                            Auswärtsspielen. Schaut gerne vorbei!
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Paper
                                    component="a"
                                    href="https://www.tbwhandball.de/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    elevation={4}
                                    sx={{
                                        display: "block",
                                        p: 3,
                                        borderRadius: 2,
                                        textDecoration: "none",
                                        color: "inherit",
                                        "&:hover": { boxShadow: 8, transform: "translateY(-2px)" },
                                        transition: "all .2s ease",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                                        <SportsHandballIcon sx={{ fontSize: 36, color: "secondary.main" }} />
                                        <Typography variant="h6">TBW Wülfrath – Handball</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        Zum Verein &amp; Spielbetrieb
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper
                                    component="a"
                                    href="https://badminton.tb-wuelfrath.de/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    elevation={4}
                                    sx={{
                                        display: "block",
                                        p: 3,
                                        borderRadius: 2,
                                        textDecoration: "none",
                                        color: "inherit",
                                        "&:hover": { boxShadow: 8, transform: "translateY(-2px)" },
                                        transition: "all .2s ease",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                                        <SportsTennisIcon sx={{ fontSize: 36, color: "primary.main" }} />
                                        <Typography variant="h6">TBW Wülfrath – Badminton</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        Zur Seite
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper
                                    component="a"
                                    href="https://tsveinigkeit.de/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    elevation={4}
                                    sx={{
                                        display: "block",
                                        p: 3,
                                        borderRadius: 2,
                                        textDecoration: "none",
                                        color: "inherit",
                                        "&:hover": { boxShadow: 8, transform: "translateY(-2px)" },
                                        transition: "all .2s ease",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                                        <SportsSoccerIcon sx={{ fontSize: 36, color: "primary.main" }} />
                                        <Typography variant="h6">Einigkeit Dornap – Fußball</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        Zur Vereinsseite &amp; Teams
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper
                                    component="a"
                                    href="https://ktk-community.de/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    elevation={4}
                                    sx={{
                                        display: "block",
                                        p: 3,
                                        borderRadius: 2,
                                        textDecoration: "none",
                                        color: "inherit",
                                        "&:hover": { boxShadow: 8, transform: "translateY(-2px)" },
                                        transition: "all .2s ease",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                                        <SportsMmaIcon sx={{ fontSize: 36, color: "primary.main" }} />
                                        <Typography variant="h6">Kyeok Too Ki</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        Zur KTK-Community
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </SectionCard>

                    {/* 5) ANFAHRT */}
                    <SectionCard id="anfahrt" title="Anfahrt" stripeColor="#eaeaea">
                        <Box sx={{ borderRadius: 2, overflow: "hidden", boxShadow: 5 }}>
                            <iframe
                                title="map"
                                src="https://www.google.com/maps?q=Wilhelmstr.161,+42489+Wülfrath&output=embed"
                                width="100%"
                                height="400"
                                style={{ border: 0 }}
                                loading="lazy"
                            />
                        </Box>
                    </SectionCard>

                    {/* 6) KONTAKT */}
                    <SectionCard id="kontakt" title="Kontakt" stripeColor="#eaeaea" mb={{ xs: 2, md: 4 }}>
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <RoomIcon color="action" />
                                <Typography component="span">{contact.address}</Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                <LocalPhoneIcon color="action" />
                                <Link
                                    href={`tel:${sanitizePhone(contact.phone)}`}
                                    underline="hover"
                                    color="inherit"
                                    sx={{ fontWeight: 500 }}
                                >
                                    {contact.phone}
                                </Link>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                <EmailIcon color="action" />
                                <Link
                                    href={`mailto:${contact.email}`}
                                    underline="hover"
                                    color="inherit"
                                    sx={{ fontWeight: 500 }}
                                >
                                    {contact.email}
                                </Link>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2 }}>
                                <IconButton
                                    aria-label="Instagram – Zum Alten Rathaus"
                                    component="a"
                                    href={socialLinks.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <InstagramIcon />
                                </IconButton>
                                <IconButton
                                    aria-label="Facebook – Mona Hartmann"
                                    component="a"
                                    href={socialLinks.facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FacebookIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <Paper
                            elevation={4}
                            sx={{
                                mt: 3,
                                p: { xs: 2, md: 3 },
                                maxWidth: 720,
                                backgroundColor: theme.palette.common.white,
                                borderRadius: 2,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                                <Typography variant="h6">Öffnungszeiten</Typography>
                                <Chip
                                    size="small"
                                    label={
                                        todayOpening.hours
                                            ? `Heute (${todayOpening.todayName}): ${todayOpening.hours}`
                                            : `Heute: siehe unten`
                                    }
                                    color={todayOpening.isClosed ? "default" : "success"}
                                    sx={{ fontSize: "0.7rem" }}
                                />
                            </Box>

                            {openingHours.map((o, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        py: 0.75,
                                    }}
                                >
                                    <Typography>{o.day}</Typography>
                                    {o.hours && <Typography sx={{ fontWeight: 500 }}>{o.hours}</Typography>}
                                </Box>
                            ))}
                        </Paper>
                    </SectionCard>
                </Container>
            </Box>

            {/* LIGHTBOX */}
            <Modal
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 300 } }}
            >
                <Fade in={lightboxOpen}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            outline: "none",
                        }}
                    >
                        <Box
                            component="img"
                            src={lightboxSrc || ""}
                            alt="Bild"
                            loading="lazy"
                            decoding="async"
                            sx={{
                                maxWidth: "90vw",
                                maxHeight: "85vh",
                                borderRadius: 2,
                                boxShadow: 8,
                            }}
                        />
                    </Box>
                </Fade>
            </Modal>

            {/* FOOTER */}
            <Box
                ref={footerRef}
                sx={{
                    bgcolor: "primary.main",
                    color: "#fff",
                    textAlign: "center",
                    py: 3,
                    mt: 4,
                }}
            >
                <Typography variant="body2">© Zum Alten Rathaus · Wülfrath</Typography>

                <Box sx={{ mt: 1 }}>
                    <Link href="#/impressum" color="inherit" underline="hover" sx={{ mx: 1, fontSize: "0.85rem" }}>
                        Impressum
                    </Link>
                    |
                    <Link href="#/datenschutz" color="inherit" underline="hover" sx={{ mx: 1, fontSize: "0.85rem" }}>
                        Datenschutz
                    </Link>
                </Box>
            </Box>

            {/* MODAL Reservierung */}
            <Modal
                open={reserveOpen}
                onClose={() => setReserveOpen(false)}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 500 } }}
            >
                <Fade in={reserveOpen}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            bgcolor: "background.paper",
                            borderRadius: 3,
                            boxShadow: 24,
                            p: 4,
                            width: "90%",
                            maxWidth: 500,
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 1.5 }}>
                            Reservierung
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3 }}>
                            Rufen Sie uns an unter {contact.phone} oder schreiben Sie an {contact.email}.
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button onClick={() => setReserveOpen(false)}>Schließen</Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>

            {/* MOBILE ACTION-BAR (Icons only, clean & modern) */}
            <Fade in={!hideMobileBar} timeout={220}>
                <Box
                    sx={{
                        position: "fixed",
                        left: 0,
                        right: 0,
                        bottom: 10,
                        display: { xs: "flex", md: "none" },
                        justifyContent: "center",
                        zIndex: 1300,
                        pointerEvents: hideMobileBar ? "none" : "auto",
                        px: 2,
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            px: 1.5,
                            py: 1,
                            borderRadius: 999,
                            backgroundColor: "rgba(255,255,255,0.4)",
                            backdropFilter: "blur(14px)",
                            border: "1px solid rgba(255,255,255,0.6)",
                            boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
                        }}
                    >

                    {[
                            {
                                label: "Anrufen",
                                href: `tel:${sanitizePhone(contact.phone)}`,
                                icon: <LocalPhoneIcon />,
                            },
                            {
                                label: "Navigation",
                                href: "https://www.google.com/maps/dir/?api=1&destination=Wilhelmstr.+161,+42489+Wülfrath",
                                icon: <RoomIcon />,
                                target: "_blank",
                            },
                            {
                                label: "Reservieren",
                                onClick: () => setReserveOpen(true),
                                icon: <RestaurantIcon />,
                            },
                            {
                                label: "Instagram",
                                href: socialLinks.instagram,
                                icon: <InstagramIcon />,
                                target: "_blank",
                            },
                        ].map((item, i) => (
                            <IconButton
                                key={i}
                                aria-label={item.label}
                                onClick={item.onClick}
                                component={item.href ? "a" : "button"}
                                href={item.href}
                                target={item.target}
                                rel={item.target ? "noopener noreferrer" : undefined}
                                sx={{
                                    width: 37,
                                    height: 37,
                                    borderRadius: "50%",
                                    color: "primary.main",
                                    backgroundColor: "rgba(255,255,255,0.6)",
                                    border: "1px solid rgba(0,0,0,0.05)",

                                    "&:hover": {
                                        backgroundColor: "rgba(156,67,67,0.12)",
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 6px 14px rgba(0,0,0,0.18)",
                                    },

                                    transition: "all .18s ease",
                                }}
                            >
                                {item.icon}
                            </IconButton>
                        ))}
                    </Paper>
                </Box>
            </Fade>


        </Box>
    );
}
